import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardCheck, CheckCircle, XCircle, Volume2, Loader2,
  RefreshCw, Plus, AlertTriangle, ChevronDown, ChevronUp,
  Radio, Globe, Zap
} from 'lucide-react';
import toast from 'react-hot-toast';
import { airportAnnouncementsAPI, announcementZonesAPI, gatesAPI, gateQueueAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/UI/PageHeader';
import EmptyState from '../../components/UI/EmptyState';
import StatusBadge from '../../components/UI/StatusBadge';
import Button from '../../components/UI/Button';

const PRIORITY_CFG = {
  normal:    { label: 'Normal',    tone: 'neutral', dot: 'bg-gray-400' },
  urgent:    { label: 'Urgent',    tone: 'orange',  dot: 'bg-orange-500' },
  emergency: { label: 'Emergency', tone: 'danger',  dot: 'bg-red-600' },
};

// 'broadcasting' was previously a one-off solid-fill badge (white text on
// bg-blue-500) — every other "happening right now" state in the app
// (ScheduledAnnouncement 'playing', GateSession 'active') uses a light tint
// + pulsing dot instead, so this now matches that convention rather than
// being its own inconsistent treatment.
const STATUS_CFG = {
  pending:      { label: 'Pending',      tone: 'yellow' },
  generating:   { label: 'Generating',   tone: 'info' },
  ready:        { label: 'Ready',        tone: 'success' },
  queued:       { label: 'Queued',       tone: 'teal' },
  broadcasting: { label: 'Broadcasting', tone: 'info', pulse: true },
  played:       { label: 'Played',       tone: 'neutral' },
  rejected:     { label: 'Rejected',     tone: 'danger' },
  cancelled:    { label: 'Cancelled',    tone: 'neutral' },
};

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

export default function OperatorApprovalsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canApprove = ['admin', 'supervisor', 'manager'].includes(user?.role);
  const [tab, setTab] = useState('pending'); // 'pending' | 'mine'
  const [pending, setPending] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState([]);
  const [gates, setGates] = useState([]);

  // Expanded item for inline approval
  const [expanded, setExpanded] = useState(null);
  const [selectedZones, setSelectedZones] = useState([]);
  const [selectedGates, setSelectedGates] = useState([]);
  // CR 8.4 — approver retains or overrides the requester's chosen playback timing.
  const [timingType, setTimingType] = useState('relative');
  const [relativeMinutes, setRelativeMinutes] = useState(0);
  const [scheduledAt, setScheduledAt] = useState('');
  const [insertAfterId, setInsertAfterId] = useState('');
  const [upcomingForGate, setUpcomingForGate] = useState([]);
  const [adminNotes, setAdminNotes] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectNote, setRejectNote] = useState('');

  const loadPending = useCallback(async () => {
    try {
      const res = await airportAnnouncementsAPI.getPending();
      setPending(res.data.data || []);
    } catch { toast.error('Failed to load pending approvals'); }
  }, []);

  const loadMine = useCallback(async () => {
    try {
      const res = await airportAnnouncementsAPI.getAll({ limit: 50 });
      setMine(res.data.data || res.data || []);
    } catch { toast.error('Failed to load your announcements'); }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadPending(), loadMine()]);
    setLoading(false);
  }, [loadPending, loadMine]);

  useEffect(() => {
    loadAll();
    Promise.all([announcementZonesAPI.getAll(), gatesAPI.getAll()])
      .then(([zRes, gRes]) => {
        setZones(zRes.data.data || []);
        setGates(gRes.data.data || []);
      })
      .catch(() => {});
  }, [loadAll]);

  // Sequence-position timing only resolves for a single-gate target (CR 7.4) —
  // fetch that gate's live queue so the approver can pick "after which item".
  const isSingleGateTarget = selectedGates.length === 1 && selectedZones.length === 0;
  useEffect(() => {
    if (!expanded || !isSingleGateTarget) { setUpcomingForGate([]); return; }
    gateQueueAPI.getQueue(selectedGates[0])
      .then(res => setUpcomingForGate(res.data.data?.upcoming || []))
      .catch(() => setUpcomingForGate([]));
  }, [expanded, isSingleGateTarget, selectedGates]);

  function selectItem(ann) {
    if (expanded?.id === ann.id) {
      setExpanded(null);
      return;
    }
    setExpanded(ann);
    // Pre-populate from existing targets if any, otherwise default to Airport Wide
    const existing = ann.targets || [];
    if (existing.length > 0) {
      setSelectedZones(existing.filter(t => t.target_type === 'zone').map(t => t.target_id));
      setSelectedGates(existing.filter(t => t.target_type === 'gate').map(t => String(t.target_id)));
    } else if (ann.suggestedGate) {
      // Ad-hoc requests submitted from a specific gate pre-select that gate —
      // the operator shouldn't have to re-specify what they already told us.
      setSelectedZones([]);
      setSelectedGates([String(ann.suggestedGate.id)]);
    } else {
      const airportWide = zones.find(z => z.zone_code === 'ALL' || z.zone_type === 'airport');
      setSelectedZones(airportWide ? [airportWide.zone_code] : []);
      setSelectedGates([]);
    }
    // Retain the requester's chosen timing by default (CR 8.4) — the approver
    // can still override any of it below before approving.
    setTimingType(ann.timing_type || 'relative');
    setRelativeMinutes(ann.relative_minutes ?? 0);
    setScheduledAt(ann.scheduled_at ? new Date(ann.scheduled_at).toISOString().slice(0, 16) : '');
    setInsertAfterId(ann.insert_after_id != null ? String(ann.insert_after_id) : '');
    setAdminNotes('');
    setPreviewUrl(null);
    setShowRejectForm(false);
    setRejectNote('');
  }

  function toggleZone(code) {
    setSelectedZones(prev => prev.includes(code) ? prev.filter(z => z !== code) : [...prev, code]);
  }

  function toggleGate(id) {
    const sid = String(id);
    setSelectedGates(prev => prev.includes(sid) ? prev.filter(g => g !== sid) : [...prev, sid]);
  }

  async function handlePreview(ann) {
    setPreviewing(true);
    setPreviewUrl(null);
    try {
      const res = await airportAnnouncementsAPI.preview(ann.id);
      setPreviewUrl(res.data.data.audioUrl);
    } catch { toast.error('Preview failed'); }
    finally { setPreviewing(false); }
  }

  async function handleApprove() {
    if (!expanded) return;
    const targets = [
      ...selectedZones.map(code => {
        const z = zones.find(z => z.zone_code === code);
        return { target_type: 'zone', target_id: code, target_label: z?.zone_name || code };
      }),
      ...selectedGates.map(gid => {
        const g = gates.find(g => String(g.id) === gid);
        return { target_type: 'gate', target_id: gid, target_label: `Gate ${g?.gate_code || gid}` };
      }),
    ];
    if (targets.length === 0) {
      toast.error('Select at least one target zone or gate');
      return;
    }
    if (timingType === 'clock' && !scheduledAt) {
      toast.error('Choose a playback time');
      return;
    }
    setApproving(true);
    try {
      await airportAnnouncementsAPI.approve(expanded.id, {
        targets,
        timing_type: timingType,
        relative_minutes: timingType === 'relative' ? Number(relativeMinutes) || 0 : undefined,
        scheduled_at: timingType === 'clock' ? new Date(scheduledAt).toISOString() : undefined,
        insert_after_id: timingType === 'sequence' ? (insertAfterId || null) : undefined,
        admin_notes: adminNotes || null,
      });
      toast.success('Approved — audio generating and broadcast queued');
      setExpanded(null);
      loadAll();
    } catch (err) {
      if (err.response?.data?.code === 'TIME_PASSED') {
        toast.error('That time has already passed — choose a new time or timing method');
      } else if (!err._toasted) {
        toast.error('Approval failed');
      }
    } finally { setApproving(false); }
  }

  async function handleReject() {
    if (!expanded) return;
    setRejecting(true);
    try {
      await airportAnnouncementsAPI.reject(expanded.id, { admin_notes: rejectNote });
      toast.success('Announcement rejected');
      setExpanded(null);
      loadAll();
    } catch (err) {
      if (!err._toasted) toast.error('Rejection failed');
    } finally { setRejecting(false); }
  }

  async function handleCancel(id) {
    if (!window.confirm('Cancel this announcement?')) return;
    try {
      await airportAnnouncementsAPI.cancel(id);
      toast.success('Cancelled');
      loadAll();
    } catch (err) {
      if (!err._toasted) toast.error('Failed to cancel');
    }
  }

  const isExpanded = (ann) => expanded?.id === ann.id;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-6">
      {/* Header */}
      <PageHeader
        title="Announcements"
        subtitle="Approve or manage airport announcements"
        actions={
          <>
            <button onClick={loadAll} className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50">
              <RefreshCw size={16} />
            </button>
            <Button variant="primary" icon={Plus} onClick={() => navigate('/operator/announce')}>
              New
            </Button>
          </>
        }
      />

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-xl p-1">
        <button
          onClick={() => setTab('pending')}
          className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
            tab === 'pending' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'
          }`}
        >
          Pending Approvals
          {pending.length > 0 && (
            <span className="ml-1.5 bg-orange-500 text-white text-xs rounded-full px-1.5 py-0.5">
              {pending.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('mine')}
          className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
            tab === 'mine' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'
          }`}
        >
          My Submissions
        </button>
      </div>

      {/* Pending Approvals Tab */}
      {tab === 'pending' && (
        <div className="space-y-2">
          {pending.length === 0 ? (
            <EmptyState icon={ClipboardCheck} title="No pending approvals" subtitle="All announcements are up to date" />
          ) : (
            pending.map(ann => {
              const pri = PRIORITY_CFG[ann.priority] || PRIORITY_CFG.normal;
              const open = isExpanded(ann);
              return (
                <div key={ann.id} className={`bg-white rounded-2xl border transition-all ${open ? 'border-blue-300 shadow-md' : 'border-gray-200'}`}>
                  {/* Summary row */}
                  <button
                    onClick={() => selectItem(ann)}
                    className="w-full text-left px-4 py-3.5 flex items-start gap-3"
                  >
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5 ${pri.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="font-semibold text-sm text-gray-900">{ann.title}</span>
                        <StatusBadge label={pri.label} tone={pri.tone} />
                      </div>
                      <p className="text-xs text-gray-500 line-clamp-1">{ann.english_text}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                        <span>By {ann.requester?.name || '—'}</span>
                        <span>·</span>
                        <span>{timeAgo(ann.createdAt || ann.created_at)}</span>
                      </div>
                    </div>
                    {open ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0 mt-1" /> : <ChevronDown size={16} className="text-gray-400 flex-shrink-0 mt-1" />}
                  </button>

                  {/* Expanded approval panel */}
                  {open && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-4">
                      {/* Full text */}
                      <div className="bg-gray-50 rounded-xl p-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Announcement Text</p>
                        <p className="text-sm text-gray-800">{ann.english_text}</p>
                      </div>

                      {/* Preview audio */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePreview(ann)}
                          disabled={previewing}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 disabled:opacity-50"
                        >
                          {previewing ? <Loader2 size={14} className="animate-spin" /> : <Volume2 size={14} />}
                          Preview Audio
                        </button>
                        {previewUrl && (
                          <audio controls src={previewUrl} className="flex-1 h-8" />
                        )}
                      </div>

                      {/* Target zone/gate selection */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Broadcast To</p>
                        {zones.length > 0 && (
                          <div className="mb-2">
                            <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Globe size={11} /> Zones</p>
                            <div className="flex flex-wrap gap-1.5">
                              {zones.map(z => (
                                <button
                                  key={z.zone_code}
                                  onClick={() => toggleZone(z.zone_code)}
                                  className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                                    selectedZones.includes(z.zone_code)
                                      ? 'bg-blue-700 text-white border-blue-700'
                                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                                  }`}
                                >
                                  {z.zone_name || z.zone_code}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {gates.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Radio size={11} /> Gates</p>
                            <div className="flex flex-wrap gap-1.5">
                              {gates.map(g => (
                                <button
                                  key={g.id}
                                  onClick={() => toggleGate(g.id)}
                                  className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                                    selectedGates.includes(String(g.id))
                                      ? 'bg-blue-700 text-white border-blue-700'
                                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                                  }`}
                                >
                                  {g.gate_code}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Schedule — CR 8.3/8.4: retain or override clock / relative / sequence timing */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Schedule</p>
                        <div className="flex gap-2 mb-2">
                          {[
                            { value: 'relative', label: 'Now / In N min' },
                            { value: 'clock', label: 'At a Time' },
                            { value: 'sequence', label: 'After Item', disabled: !isSingleGateTarget },
                          ].map(m => (
                            <button
                              key={m.value}
                              onClick={() => !m.disabled && setTimingType(m.value)}
                              disabled={m.disabled}
                              title={m.disabled ? 'Only available when targeting a single gate' : undefined}
                              className={`flex-1 py-2 rounded-xl text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                timingType === m.value
                                  ? 'bg-blue-700 text-white border-blue-700'
                                  : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                              }`}
                            >
                              {m.label}
                            </button>
                          ))}
                        </div>

                        {timingType === 'relative' && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">In</span>
                            <input
                              type="number"
                              min={0}
                              value={relativeMinutes}
                              onChange={e => setRelativeMinutes(e.target.value)}
                              className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-600">min — 0 = broadcast now</span>
                          </div>
                        )}
                        {timingType === 'clock' && (
                          <input
                            type="datetime-local"
                            value={scheduledAt}
                            onChange={e => setScheduledAt(e.target.value)}
                            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        )}
                        {timingType === 'sequence' && (
                          <select
                            value={insertAfterId}
                            onChange={e => setInsertAfterId(e.target.value)}
                            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Play first, before anything queued</option>
                            {upcomingForGate.map(a => (
                              <option key={a.id} value={a.id}>
                                After: {(a.english_text || a.announcement_type || '').slice(0, 60)}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      {/* Notes */}
                      <textarea
                        value={adminNotes}
                        onChange={e => setAdminNotes(e.target.value)}
                        placeholder="Approval notes (optional)..."
                        rows={2}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      />

                      {/* Approve / Reject — supervisor/manager/admin only, and never your own request */}
                      {!canApprove ? (
                        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-center">
                          Awaiting supervisor or manager approval
                        </p>
                      ) : expanded?.requester?.id === user?.id ? (
                        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-center">
                          You submitted this request — another approver must review it
                        </p>
                      ) : !showRejectForm ? (
                        <div className="flex gap-2">
                          <Button variant="success" icon={CheckCircle} loading={approving} onClick={handleApprove} size="lg" className="flex-1">
                            Approve
                          </Button>
                          <Button variant="dangerLight" icon={XCircle} onClick={() => setShowRejectForm(true)} size="lg">
                            Reject
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <textarea
                            value={rejectNote}
                            onChange={e => setRejectNote(e.target.value)}
                            placeholder="Reason for rejection..."
                            rows={2}
                            className="w-full border border-red-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                          />
                          <div className="flex gap-2">
                            <Button variant="danger" icon={XCircle} loading={rejecting} onClick={handleReject} size="lg" className="flex-1">
                              Confirm Reject
                            </Button>
                            <Button variant="ghost" onClick={() => setShowRejectForm(false)} size="lg">
                              Back
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* My Submissions Tab */}
      {tab === 'mine' && (
        <div className="space-y-2">
          {mine.length === 0 ? (
            <EmptyState
              icon={Radio}
              title="No announcements yet"
              actionLabel="Submit your first announcement"
              onAction={() => navigate('/operator/announce')}
            />
          ) : (
            mine.map(item => {
              const meta = STATUS_CFG[item.status] || STATUS_CFG.pending;
              const pri = PRIORITY_CFG[item.priority] || PRIORITY_CFG.normal;
              const isCancellable = ['pending'].includes(item.status);
              return (
                <div key={item.id} className="bg-white rounded-2xl border border-gray-200 px-4 py-3.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${pri.dot}`} />
                        <span className="font-semibold text-sm text-gray-900 truncate">{item.title}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusBadge label={meta.label} tone={meta.tone} dot={meta.pulse} pulse={meta.pulse} />
                        <span className="text-xs text-gray-400 capitalize">
                          {item.announcement_type?.replace('_', ' ')}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(item.created_at || item.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {item.admin_notes && (
                        <p className="text-xs text-red-600 mt-1.5 italic">Note: {item.admin_notes}</p>
                      )}
                    </div>
                    {isCancellable && (
                      <button
                        onClick={() => handleCancel(item.id)}
                        className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Cancel"
                      >
                        <XCircle size={16} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
